// ============================================
// DataTable Component
// Generic data table with configurable columns
// ============================================

import { ReactNode } from 'react';
import { cn } from '../../utils/cn';
import { MoreVerticalIcon, FilterIcon } from '../icons';

// ============================================
// Types
// ============================================

export interface Column<T> {
  /** Unique key for the column */
  key: string;
  /**
   * Nội dung ô tiêu đề cột.
   *
   * `ReactNode` chứ không phải `string`: cột chọn hàng loạt cần đặt một ô tích
   * "chọn cả trang" ngay trong header. Ô header vốn đã render qua `{col.header}`
   * nên nới kiểu là đủ, không phải đổi chỗ nào khác.
   */
  header: ReactNode;
  /** Optional sub-header text */
  subHeader?: string;
  /** Column width (CSS value) */
  width?: string;
  /** Text alignment */
  align?: 'left' | 'center' | 'right';
  /** Custom cell renderer */
  render?: (row: T, index: number) => ReactNode;
}

interface DataTableProps<T> {
  /** Column configuration */
  columns: Column<T>[];
  /** Data rows */
  data: T[];
  /** Unique key accessor for each row */
  rowKey: keyof T | ((row: T) => string);
  /** Table title */
  title?: string;
  /** Show filter icon in header */
  showFilter?: boolean;
  /** Show kebab menu in header */
  showHeaderMenu?: boolean;
  /** Filter click handler */
  onFilterClick?: () => void;
  /** Header menu click handler */
  onHeaderMenuClick?: () => void;
  /** Row click handler */
  onRowClick?: (row: T) => void;
  /** Loading state */
  isLoading?: boolean;
  /** Empty state message */
  emptyMessage?: string;
  /** Additional className */
  className?: string;
}

// ============================================
// Component
// ============================================

export function DataTable<T>({
  columns,
  data,
  rowKey,
  title,
  showFilter = true,
  showHeaderMenu = true,
  onFilterClick,
  onHeaderMenuClick,
  onRowClick,
  isLoading,
  emptyMessage = 'No data found',
  className,
}: DataTableProps<T>) {
  const getRowKey = (row: T, index: number): string => {
    if (typeof rowKey === 'function') {
      return rowKey(row);
    }
    return String(row[rowKey] ?? index);
  };

  return (
    <div
      className={cn(
        'overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm',
        className
      )}
    >
      {/* Table Header */}
      {(title || showFilter || showHeaderMenu) && (
        <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/50 px-6 py-4">
          {title && (
            <h3 className="text-base font-bold text-slate-900">{title}</h3>
          )}
          <div className="flex items-center gap-2">
            {showFilter && (
              <button
                onClick={onFilterClick}
                className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
                aria-label="Filter"
              >
                <FilterIcon size={18} />
              </button>
            )}
            {showHeaderMenu && (
              <button
                onClick={onHeaderMenuClick}
                className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
                aria-label="More options"
              >
                <MoreVerticalIcon size={18} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          {/* Column Headers */}
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50/50">
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={cn(
                    'whitespace-nowrap px-6 py-3 text-left text-[11px] font-bold uppercase tracking-[0.55px] text-slate-500',
                    col.align === 'center' && 'text-center',
                    col.align === 'right' && 'text-right'
                  )}
                  style={{ width: col.width }}
                >
                  <span>{col.header}</span>
                  {col.subHeader && (
                    <span className="ml-1 font-normal text-slate-400">
                      {col.subHeader}
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>

          {/* Table Body */}
          <tbody>
            {isLoading ? (
              // Loading skeleton
              Array.from({ length: 5 }).map((_, idx) => (
                <tr key={idx} className="border-t border-slate-100">
                  {columns.map((col) => (
                    <td key={col.key} className="px-6 py-4">
                      <div className="h-4 w-3/4 animate-pulse rounded bg-slate-200" />
                    </td>
                  ))}
                </tr>
              ))
            ) : data.length === 0 ? (
              // Empty state
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-6 py-12 text-center text-sm text-slate-500"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              // Data rows
              data.map((row, rowIdx) => (
                <tr
                  key={getRowKey(row, rowIdx)}
                  className={cn(
                    'group border-t border-slate-100 transition-colors',
                    onRowClick && 'cursor-pointer hover:bg-slate-50'
                  )}
                  onClick={() => onRowClick?.(row)}
                >
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={cn(
                        // `align-top` + `break-words`: ô có nội dung dài (góp ý
                        // khách, ghi chú) trước đây kéo giãn cột và đẩy nội dung
                        // các ô khác chồng lên nhau (bug #247). Căn trên để các
                        // ô một dòng không bị dạt xuống giữa hàng cao.
                        'break-words px-6 py-4 align-top text-sm text-slate-700',
                        col.align === 'center' && 'text-center',
                        col.align === 'right' && 'text-right'
                      )}
                    >
                      {col.render
                        ? col.render(row, rowIdx)
                        : String((row as Record<string, unknown>)[col.key] ?? '')}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
